import { measureLatency } from '../../gctl-measure';
import {
  SubscriptionPlansFields,
  SubscriptionPlans,
} from '../../models/components/schemas/subscription-plans/SubscriptionPlans';
import {
  Select,
  NetworkError,
  deepCamelCase,
  searchQueryString,
  intoSnakeCase,
} from '../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetMeSubscriptionPlansFields = readonly SubscriptionPlansFields[];

/** Options for GET /me/subscription_plans */
export type GetMeSubscriptionPlansOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;

  /** Possible query parameters for /me/subscription_plans */
  query?: {
    /**
     * Whether the user can view the available subscription tiers up to and including their current tier.
     */
    bypassTierHierarchy?: boolean;

    /**
     * The ID of the campaign.
     */
    campaignId?: string;

    /**
     * The country code of the subscription product.
     */
    country?: string;

    /**
     * The currency in which the price of the subscription product is expressed.
     */
    currency?: string;

    /**
     * The type of subscription plan or plans to filter on. Use a comma-separated list of values to filter multiple plan types.
     *
     * Option descriptions:
     *  * `advanced` - The plan type is Vimeo Advanced.
     *  * `business` - The plan type is Vimeo Business.
     *  * `custom_self_serve` - The plan type is Vimeo Custom.
     *  * `free` - The plan type is Vimeo Free.
     *  * `live_premium` - The plan type is Vimeo Premium.
     *  * `plus` - The plan type is Vimeo Plus.
     *  * `pro` - The plan type is Vimeo Pro.
     *  * `pro_solution` - The plan type is Vimeo Pro Solution.
     *  * `pro_unlimited` - The plan type is Vimeo Pro Unlimited.
     *  * `standard` - The plan type is Vimeo Standard.
     *  * `starter` - The plan type is Vimeo Starter.
     *  * `team` - The plan type is Vimeo Team.
     *  * `team_live` - The plan type is Vimeo Team Live.
     *
     */
    filter?: (
      | `advanced`
      | `business`
      | `custom_self_serve`
      | `free`
      | `live_premium`
      | `plus`
      | `pro`
      | `pro_solution`
      | `pro_unlimited`
      | `standard`
      | `starter`
      | `team`
      | `team_live`
    )[];

    /**
     * The ID of the promotional code.
     */
    promoCodeId?: string;

    /**
     * The URL-encoded promotion code map given as a JSON array, such as `{'plus': 'RENEWPLUS'}`.
     */
    promos?: string;

    /**
     * The type of subscription plan or plans to filter on. Use a comma-separated list of values to filter multiple plan types.
     *
     * Option descriptions:
     *  * `advanced` - The plan type is Vimeo Advanced.
     *  * `business` - The plan type is Vimeo Business.
     *  * `custom_self_serve` - The plan type is Vimeo Custom.
     *  * `free` - The plan type is Vimeo Free.
     *  * `live_premium` - The plan type is Vimeo Premium.
     *  * `plus` - The plan type is Vimeo Plus.
     *  * `pro` - The plan type is Vimeo Pro.
     *  * `pro_solution` - The plan type is Vimeo Pro Solution.
     *  * `pro_unlimited` - The plan type is Vimeo Pro Unlimited.
     *  * `standard` - The plan type is Vimeo Standard.
     *  * `starter` - The plan type is Vimeo Starter.
     *  * `team` - The plan type is Vimeo Team.
     *  * `team_live` - The plan type is Vimeo Team Live.
     *
     */
    tiers?: (
      | `advanced`
      | `business`
      | `custom_self_serve`
      | `free`
      | `live_premium`
      | `plus`
      | `pro`
      | `pro_solution`
      | `pro_unlimited`
      | `standard`
      | `starter`
      | `team`
      | `team_live`
    )[];

    /**
     * Whether to use the Vimeo payments service instead of Vimeo Products.
     */
    usePaymentsService?: boolean;

    /**
     * The value used to test pricing SKUs. This value must be a VUID hash. _This field is required when a pricing test is run._
     */
    vuid?: string;
  };
};

/** Result type for GET /me/subscription_plans */
export type GetMeSubscriptionPlansResult<Paths extends readonly string[]> = Select<
  SubscriptionPlans,
  Paths
>[];

/**
 * Get all the subscription plans that the user can upgrade to
 * This method returns every subscription plan that the authenticated user can upgrade to.
 */
export async function getMeSubscriptionPlans<Paths extends GetMeSubscriptionPlansFields>({
  baseUrl,
  select,
  query,
  ...options
}: GetMeSubscriptionPlansOptions<Paths>): Promise<GetMeSubscriptionPlansResult<Paths>> {
  return measureLatency('getMeSubscriptionPlans', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/me/subscription_plans?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetMeSubscriptionPlansResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
